// Return-to-page after sign-in: the pure rules.
//
// A signed-out visitor who opens a protected page is sent to
// `/signin?next=<the page>`, and after signing in lands back on it, path,
// query and hash intact. That matters most for OAuth-style callbacks
// (GitHub App install, Microsoft admin consent) whose query carries the
// one-time `code` / `state` the page must read.
//
// `next` is attacker-controllable (anyone can link to /signin?next=...),
// so it is only honoured when it is a same-origin relative path. Anything
// else falls back to the normal post-sign-in home.

/** Query parameter the sign-in page reads the return target from. */
export const RETURN_TO_PARAM = 'next';

/** Long enough for a callback URL with a few tokens; anything longer is
 *  not a page we sent someone to. */
export const MAX_RETURN_TO_LENGTH = 2048;

// Pages that must never be a return target: sending someone back to the
// sign-in / sign-up / sign-out pages would loop or undo the sign-in.
const BLOCKED_PATHS = ['/signin', '/signup', '/login', '/logout', '/signout'];

// C0 controls, DEL and C1 controls. URL parsing silently strips tabs and
// newlines, which is how `/\t/evil.com` style tricks get through naive checks.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;

function isBlockedPath(pathname: string): boolean {
  const p = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return BLOCKED_PATHS.some((b) => p === b || p.startsWith(b + '/'));
}

/** The validated return target (`pathname + search + hash`), or null when
 *  `raw` is missing, too long, not a same-origin relative path, or points
 *  at a sign-in / sign-out page. `origin` is `window.location.origin` in
 *  the app; it is a parameter so the rules are testable without a DOM. */
export function safeReturnTo(raw: unknown, origin: string): string | null {
  if (typeof raw !== 'string') return null;
  if (raw.length === 0 || raw.length > MAX_RETURN_TO_LENGTH) return null;
  // Exactly one leading slash: `//host` and `/\host` are protocol-relative
  // to a browser, and anything without a slash may carry a scheme.
  if (raw[0] !== '/' || raw[1] === '/' || raw.includes('\\')) return null;
  if (CONTROL_CHARS.test(raw)) return null;

  let base: URL;
  let url: URL;
  try {
    base = new URL(origin);
    url = new URL(raw, base);
  } catch {
    return null;
  }
  if (url.origin !== base.origin) return null;

  // An encoded slash or backslash at the start of the path (`/%2F/evil.com`)
  // is harmless to the router but is exactly what a later decode step could
  // turn into `//evil.com`, so refuse it outright.
  let decodedPath: string;
  try {
    decodedPath = decodeURIComponent(url.pathname);
  } catch {
    return null;
  }
  if (decodedPath.startsWith('//') || decodedPath.includes('\\')) return null;
  if (isBlockedPath(decodedPath)) return null;

  return url.pathname + url.search + url.hash;
}

/** Where a protected route sends a signed-out visitor: `/signin`, carrying
 *  the page they asked for as `next` when it is a valid return target. */
export function signInPathFor(
  location: { pathname: string; search?: string; hash?: string },
  origin: string,
): string {
  const target = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
  // The bare root is the default landing anyway; no need to carry it.
  if (target === '/') return '/signin';
  const safe = safeReturnTo(target, origin);
  return safe ? `/signin?${RETURN_TO_PARAM}=${encodeURIComponent(safe)}` : '/signin';
}
